import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CloseIcon from "@mui/icons-material/Close";
import { KotCoachResultDialog } from "../../components/KotCoachResultDialog";
import {
  formatKotLeaderboardVictories,
  formatKotLeaderboardVictoriesShort,
  KOT_LEADERBOARD_RANK_MODES,
  kotLeaderboardGamePointsScored,
  kotLeaderboardRankingPoints,
  sortKotLeaderboardRows,
} from "../../utils/kotCoachRound";
import { resolvePlayerDisplayFields, formatPlayerName } from "../../utils/liveTrainingPlayerContext";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";

export function KotPlayerLeaderboardList({
  rows = [],
  myPlayerId = null,
  roster = [],
  matches = [],
  maxRows = 8,
  compact = false,
  rankMode = KOT_LEADERBOARD_RANK_MODES.VICTORIES,
}) {
  const { t } = useTranslation();
  const sortedRows = React.useMemo(
    () => sortKotLeaderboardRows(rows, rankMode, matches),
    [rows, rankMode, matches]
  );
  const list = sortedRows.slice(0, maxRows > 0 ? maxRows : sortedRows.length);
  const winsPrimaryClass =
    rankMode === KOT_LEADERBOARD_RANK_MODES.VICTORIES
      ? " lt-kot-lb__stat--primary"
      : "";
  const ptsPrimaryClass =
    rankMode === KOT_LEADERBOARD_RANK_MODES.POINTS
      ? " lt-kot-lb__stat--primary"
      : "";

  if (!list.length) {
    return (
      <p className="lt-kot-lb__empty">{t("kotLeaderboardEmpty") || "No scores yet."}</p>
    );
  }

  return (
    <ol
      className={`lt-kot-lb__list${
        compact ? " lt-kot-lb__list--compact" : ""
      }`}
    >
      {list.map((row, i) => {
        const name =
          formatPlayerName(row, roster, row.player_id) ||
          resolvePlayerDisplayFields(row, roster, row.player_id).name ||
          row.user_alias ||
          (row.player_id ? String(row.player_id).slice(-6) : "—");
        const isMe =
          myPlayerId && String(row.player_id) === String(myPlayerId);
        return (
          <li
            key={row.player_id || row.kot_score_id || i}
            className={`lt-kot-lb__row${isMe ? " lt-kot-lb__row--me" : ""}`}
          >
            <span className="lt-kot-lb__rank">{i + 1}</span>
            <span className="lt-kot-lb__name">{name}</span>
            <span className={`lt-kot-lb__wins${winsPrimaryClass}`}>
              {compact
                ? formatKotLeaderboardVictoriesShort(row, t)
                : formatKotLeaderboardVictories(row, t)}
            </span>
            <span className={`lt-kot-lb__pts${ptsPrimaryClass}`}>
              {rankMode === KOT_LEADERBOARD_RANK_MODES.POINTS
                ? kotLeaderboardGamePointsScored(row, matches)
                : kotLeaderboardRankingPoints(row)}{" "}
              pts
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function KotPlayerResultPanel({ open, match, submitHook, leaderboard }) {
  const { t } = useTranslation();
  if (!open || !match) return null;

  return (
    <div className="lt-kot-panel">
      <div className="lt-kot-panel__badge">
        <EmojiEventsIcon />
        <span>{t("liveTrainingKoTSubmit")}</span>
      </div>
      <KotCoachResultDialog
        open={open}
        match={match}
        leaderboard={leaderboard}
        coach={submitHook}
      />
    </div>
  );
}

export function KotPlayerLeaderboard({
  open,
  rows,
  matches = [],
  onDismiss,
  myPlayerId,
  roster = [],
  autoDismissMs = 10000,
  maxRows = 8,
}) {
  const { t } = useTranslation();
  const [rankMode, setRankMode] = React.useState(
    KOT_LEADERBOARD_RANK_MODES.VICTORIES
  );

  React.useEffect(() => {
    if (!open || autoDismissMs == null || autoDismissMs <= 0) return undefined;
    const id = window.setTimeout(onDismiss, autoDismissMs);
    return () => window.clearTimeout(id);
  }, [open, onDismiss, autoDismissMs]);

  React.useEffect(() => {
    if (!open) {
      setRankMode(KOT_LEADERBOARD_RANK_MODES.VICTORIES);
    }
  }, [open]);

  if (!open || !rows?.length || typeof document === "undefined") return null;

  const sheet = (
    <>
      <div
        className="lt-kot-lb__backdrop"
        role="presentation"
        onClick={onDismiss}
      />
      <div
        className="lt-kot-lb"
        role="dialog"
        aria-modal="true"
        aria-label={t("kotLeaderboard")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lt-kot-lb__head">
          <h3 className="lt-kot-lb__title">
            <EmojiEventsIcon /> {t("classement") || t("kotLeaderboard")}
          </h3>
          <button
            type="button"
            className="lt-kot-lb__close"
            onClick={onDismiss}
            aria-label={t("close")}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div
          className="lt-kot-lb__mode"
          role="group"
          aria-label={t("kotClassementModeLabel") || "Rank by"}
        >
          <button
            type="button"
            className={`lt-kot-lb__mode-btn${
              rankMode === KOT_LEADERBOARD_RANK_MODES.VICTORIES
                ? " lt-kot-lb__mode-btn--active"
                : ""
            }`}
            aria-pressed={rankMode === KOT_LEADERBOARD_RANK_MODES.VICTORIES}
            onClick={() =>
              setRankMode(KOT_LEADERBOARD_RANK_MODES.VICTORIES)
            }
          >
            {t("kotClassementModeVictories") || "Wins"}
          </button>
          <button
            type="button"
            className={`lt-kot-lb__mode-btn${
              rankMode === KOT_LEADERBOARD_RANK_MODES.POINTS
                ? " lt-kot-lb__mode-btn--active"
                : ""
            }`}
            aria-pressed={rankMode === KOT_LEADERBOARD_RANK_MODES.POINTS}
            onClick={() => setRankMode(KOT_LEADERBOARD_RANK_MODES.POINTS)}
          >
            {t("kotClassementModePoints") || "Points"}
          </button>
        </div>
        <KotPlayerLeaderboardList
          rows={rows}
          matches={matches}
          myPlayerId={myPlayerId}
          roster={roster}
          maxRows={maxRows}
          rankMode={rankMode}
        />
        <button type="button" className="lt-kot-lb__dismiss" onClick={onDismiss}>
          {t("close")}
        </button>
      </div>
    </>
  );

  return createPortal(sheet, getLiveTrainingPortalRoot() || document.body);
}

export default KotPlayerResultPanel;
